import Label from '@/components/elements/Label/Label';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers/general';
import { isEmpty } from '@/helpers/objects';
import { _s, _t } from '@/locale';
import { Fragment } from 'react/jsx-runtime';
import Card from '../Card';
import { STRETCHED_BUTTON } from '../Card.constants';
import { MIDDLE_DOT_STYLE } from './ServiceListCard.constants';
import {
  getFormattedDiscountedPrice,
  getFormattedPrice,
  getPriceLabel,
  getValidPriceTypeForDiscount,
} from './ServiceListCard.helper';
import { ServiceCardProps } from './ServiceListCard.types';

const DOT_SEPARATOR = `${MIDDLE_DOT_STYLE} items-center before:shrink-0`;

/**
 * Renders the narrow and wide wording of a value; the card collapses below 600px wide. One node
 * when both read the same, so a screen reader doesn't hear it twice.
 */
const ResponsiveText = ({ narrow, wide }: { narrow?: string; wide?: string }) =>
  narrow === wide ? (
    <>{wide}</>
  ) : (
    <>
      <span className="@[600px]:hidden">{narrow}</span>
      <span className="@[600px]:inline hidden">{wide}</span>
    </>
  );

const ServiceListCard = ({
  id,
  name,
  highlightedName,
  description,
  subtTitle,
  onShowReadMore,
  bundle,
  employeeName,
  duration,
  price,
  discount = {},
  firstAvailableTime,
  onRowClick,
  showInfoCta,
  rightSlot,
  alignPriceColumn,
  hidePriceLabel,
  leftSlot,
  tags = {},
  timeslot = {},
}: ServiceCardProps) => {
  const validPriceTypeForDiscount = getValidPriceTypeForDiscount(price, discount.amounts);
  const hasLabels = tags.wellnessCard || discount.percentage || tags.waitingList || !isEmpty(timeslot);
  const hasDiscounts = Boolean(discount.amounts?.length) || (Boolean(discount.validity) && price?.type === 'variable');
  const showPrice = price && (price.type !== 'variable' || hasDiscounts);

  const formatOriginalPrice = (isMobileView: boolean) =>
    price
      ? getFormattedPrice({
          type: price.type,
          value: price.value,
          isMobileView,
          hasDiscounts: hasDiscounts && validPriceTypeForDiscount,
          hidePriceLabel,
        })
      : undefined;
  const formatDiscountedPrice = (isMobileView: boolean) =>
    getFormattedDiscountedPrice(hasDiscounts, price, discount.amounts ?? [], isMobileView);

  const originalFormattedPrice = { narrow: formatOriginalPrice(true), wide: formatOriginalPrice(false) };
  const discountedFormattedPrice = { narrow: formatDiscountedPrice(true), wide: formatDiscountedPrice(false) };
  const showDiscountedPrice = hasDiscounts && (discountedFormattedPrice.narrow || discountedFormattedPrice.wide);

  const bundleQuantityLabel = bundle?.quantity ? _s('buyBundles.quantity', { count: bundle.quantity }) : undefined;
  const validity = bundle?.validity ?? discount.validity;

  const renderDuration = (className?: string) => (
    <span className={classnames('text-fg_secondary whitespace-nowrap font-bold', className)}>{duration}</span>
  );

  const renderDot = (className?: string) => (
    <span
      className={classnames(
        'bg-fg_secondary h-xxs w-xxs @[600px]:inline-block hidden shrink-0 rounded-full',
        className,
      )}
    />
  );

  const renderMeta = () => {
    if (!duration && !bundle && !discount.validity) {
      return null;
    }

    const metaItems = [
      duration ? renderDuration('hidden @[600px]:inline') : null,
      bundle?.quantity ? (
        <span className={classnames('text-m text-fg_secondary @[600px]:inline hidden font-bold')}>
          {bundleQuantityLabel}
        </span>
      ) : null,
      validity ? <span className={classnames('text-fg_secondary text-m whitespace-nowrap')}>{validity}</span> : null,
    ].filter(Boolean);

    return (
      <div className="text-m gap-2xs flex items-center">
        <div className={classnames('flex items-center')}>
          {metaItems.map((item, index) => {
            if (index === 0) {
              return <Fragment key={index}>{item}</Fragment>;
            }
            return (
              <Fragment key={index}>
                {renderDot('@[600px]:mx-2xs')}
                {item}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const showDetailsInfo = Boolean(onRowClick && showInfoCta);
  // Without a details view behind the row click, the description is reached through the read-more modal instead.
  const showReadMoreInfo = !showDetailsInfo && Boolean(description && onShowReadMore);
  const showInfo = showDetailsInfo || showReadMoreInfo;

  const handleShowReadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onShowReadMore(name, description, id);
  };

  const renderInfo = (className: string) => {
    const infoClassName = classnames(
      'align-center text-m gap-2xs text-fg_link shrink-0 items-center whitespace-nowrap',
      className,
    );
    const content = (
      <>
        <span>{_t('moreInfo')}</span>
        <Icon variant="info-circle" color="fg_link" size="xs" />
      </>
    );

    return showReadMoreInfo ? (
      <button type="button" onClick={handleShowReadMore} className={classnames('relative z-10', infoClassName)}>
        {content}
      </button>
    ) : (
      <div className={infoClassName}>{content}</div>
    );
  };

  const renderDetails = () => (
    <div className="flex justify-between">
      <div className="gap-sm flex min-w-0 flex-1">
        {leftSlot && <div className="@[600px]:hidden flex">{leftSlot}</div>}
        <div className="gap-xs @[600px]:justify-start flex min-w-0 flex-1 flex-col justify-center overflow-hidden">
          <header className="flex w-full flex-row justify-between">
            <h3 className="text-l text-fg_primary min-w-0 break-words">
              {onRowClick ? (
                <button type="button" onClick={onRowClick} className={STRETCHED_BUTTON}>
                  {highlightedName ?? name}
                </button>
              ) : (
                highlightedName ?? name
              )}
            </h3>
            {showInfo ? renderInfo('flex @[600px]:hidden') : null}
          </header>
          <div className="gap-2xs flex w-full flex-row justify-between">
            <div className={'gap-2xs align-center flex flex-row items-center'}>
              {employeeName && <span className="text-m text-fg_secondary">{employeeName}</span>}
              {renderMeta()}
              {firstAvailableTime && (
                <>
                  {duration && renderDot()}
                  <span className="text-m text-fg_positive">{firstAvailableTime}</span>
                </>
              )}
            </div>
            {showInfo ? renderInfo('hidden @[600px]:flex') : null}
          </div>
          {subtTitle}
        </div>
      </div>
    </div>
  );

  const renderCapacityLabel = () => {
    if (timeslot.fullyBooked) {
      return <Label variant="fully-booked" size="lg" />;
    }
    if (!timeslot.capacity) {
      return null;
    }
    if (timeslot.capacity > 3) {
      return <Label variant="available" size="lg" />;
    }

    return (
      <Label
        variant="spotsLeft"
        size="lg"
        label={_t(timeslot.capacity === 1 ? 'availableCapacity_one' : 'availableCapacity_other', {
          count: timeslot.capacity,
        })}
      />
    );
  };

  const renderLabels = () => {
    if (!hasLabels) {
      return null;
    }

    return (
      <div className="gap-sm flex flex-wrap">
        {renderCapacityLabel()}
        {timeslot.from && <Label variant="time" size="lg" label={timeslot.from} />}
        {tags.waitingList && <Label variant="fully-booked" label={_t('fullBookedWaitlist')} />}
        {tags.wellnessCard && <Label size="lg" variant="wellness-card" icon={false} label={_t('wellness')} />}
        {discount.percentage && <Label variant="smt" label={`-${discount.percentage}%`} icon={{ variant: 'smt' }} />}
      </div>
    );
  };

  const renderPrice = () => {
    if (!showPrice) {
      return null;
    }

    const showPriceLabel = !hidePriceLabel && (!hasDiscounts || price.type === 'variable');

    return (
      <div className="gap-xs @[600px]:gap-2xs @[600px]:flex-col @[600px]:items-end @[600px]:justify-center flex items-center">
        {showPriceLabel && (
          <span className="text-s text-fg_secondary @[600px]:block hidden w-full text-right">
            {getPriceLabel(price.type, hasDiscounts)}
          </span>
        )}
        {(originalFormattedPrice.narrow || originalFormattedPrice.wide) && (
          <span
            className={classnames({
              'text-fg_secondary line-through': showDiscountedPrice,
              'text-m font-bold': !showDiscountedPrice,
            })}>
            <ResponsiveText {...originalFormattedPrice} />
          </span>
        )}
        {/* Ahead of the struck-through price when collapsed, under it once the card is 600px wide. */}
        {showDiscountedPrice && (
          <span className="text-m text-fg_highlight @[600px]:order-none order-first font-bold">
            <ResponsiveText {...discountedFormattedPrice} />
          </span>
        )}
      </div>
    );
  };

  const renderCollapsedDurationAndBundle = () => {
    if (!duration && !bundle?.quantity) return null;

    return (
      <div className="@[600px]:hidden flex">
        {duration && renderDuration()}
        {bundle?.quantity && (
          <span className={classnames('text-s text-fg_secondary ml-2xs flex font-bold', { [DOT_SEPARATOR]: duration })}>
            {bundleQuantityLabel}
          </span>
        )}
      </div>
    );
  };

  const renderPriceColumn = () => (
    <div
      className={classnames('border-border_regular @[600px]:border-l', {
        'group-active:border-border_bold': onRowClick,
        '@[600px]:border-t-0 border-t': showPrice || duration || bundle?.quantity || rightSlot,
      })}>
      <div
        className={classnames(
          'text-s gap-sm pt-sm @[600px]:pl-sm @[600px]:pt-0 flex h-full items-center justify-between',
          {
            '@[600px]:min-w-[164px] @[600px]:justify-end': alignPriceColumn,
            '@[600px]:justify-start': !alignPriceColumn,
          },
        )}>
        <div className="gap-2xs flex flex-col justify-center">
          {renderPrice()}
          {renderCollapsedDurationAndBundle()}
        </div>
        {rightSlot && <div className="relative z-10">{rightSlot}</div>}
      </div>
    </div>
  );

  return (
    <Card
      size="sm"
      // The card is the container its @[600px]: variants query — its own width, not the viewport's.
      className={classnames('@container', { 'hover:drop-shadow-md active:drop-shadow-md': onRowClick })}
      cardBackground={
        onRowClick
          ? 'group relative bg-surface_l2 border border-transparent hover:border-border_bold active:bg-bg_secondary_hover'
          : undefined
      }>
      <div className="gap-sm @[600px]:flex-row flex flex-col">
        <div className="flex min-w-0 flex-1 items-center justify-between">
          <div
            className={classnames('gap-sm flex flex-1 overflow-hidden', {
              'items-start': hasLabels,
              'items-center': !hasLabels,
            })}>
            {leftSlot && <div className="@[600px]:flex hidden">{leftSlot}</div>}
            <div className="gap-xs flex min-w-0 flex-1 flex-col">
              {renderLabels()}
              {renderDetails()}
            </div>
          </div>
        </div>
        {renderPriceColumn()}
      </div>
    </Card>
  );
};

export default ServiceListCard;
